/**
 * User self APIs – logged-in user's own records only (api/user/...)
 */

import { apiClient } from '../client'

const wrap = <T>(res: { data?: T }) => res.data

// --- User Questionnaire (job assessment) ---
export interface UserQuestionnaireRecord {
  id: number
  user_id: number
  question_en?: string | null
  question_ar?: string | null
  type?: string | null
  options?: string | null
  options_ar?: string | null
  is_merchandise?: boolean
  is_promoter?: boolean
  is_L4?: boolean
  is_L5?: boolean
  status?: string | null
  [key: string]: unknown
}

/** Section for grouping questionnaire questions. */
export interface QuestionnaireSection {
  id: number
  name: string
  sort_order: number
}

/** Question item for the form (from GET questions). Includes section/parent for layout and display_child_if for conditional children. */
export interface QuestionnaireQuestion {
  id: number
  question_en?: string | null
  question_ar?: string | null
  type?: string | null
  options?: string | null
  options_ar?: string | null
  status?: string | null
  section_id?: number | null
  parent_id?: number | null
  section?: QuestionnaireSection | null
  [key: string]: unknown
}

export interface UserQuestionnaireAnswerRecord {
  id: number
  question_id: number
  user_id: number
  answer?: string | null
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export const userQuestionnaireSelfRepository = {
  getMy: () =>
    apiClient.get<{ data: UserQuestionnaireRecord }>('/api/user/user-questionnaires').then(wrap),
  /** Sections for questionnaire (ordered by sort_order). */
  getSections: () =>
    apiClient.get<{ data: QuestionnaireSection[] }>('/api/user/user-questionnaires/sections').then(wrap),
  /** Active questions for the form (with section_id, parent_id, display_child_if, section). Optional filters: isl4, isl5, ismerchandiser (pass 1 or true). */
  getQuestions: (params?: { isl4?: boolean | number; isl5?: boolean | number; ismerchandiser?: boolean | number }) => {
    const search = new URLSearchParams()
    if (params?.isl4) search.set('isl4', '1')
    if (params?.isl5) search.set('isl5', '1')
    if (params?.ismerchandiser) search.set('ismerchandiser', '1')
    const qs = search.toString()
    return apiClient
      .get<{ data: QuestionnaireQuestion[] }>(`/api/user/user-questionnaires/questions${qs ? `?${qs}` : ''}`)
      .then(wrap)
  },
  upsertMy: (data: Partial<UserQuestionnaireRecord>) =>
    apiClient.post<{ data: UserQuestionnaireRecord }>('/api/user/user-questionnaires', data).then(res => res.data),
  replaceMy: (data: Partial<UserQuestionnaireRecord>) =>
    apiClient.put<{ data: UserQuestionnaireRecord }>('/api/user/user-questionnaires', data).then(res => res.data),
  deleteMy: () =>
    apiClient.delete<{ message: string }>('/api/user/user-questionnaires'),
}

/** Completion stats: of user's rows in user_questionnaire_answers, % with non-null, non-empty answer. */
export interface UserQuestionnaireCompletionRecord {
  filled_count: number
  total_count: number
  percentage: number
}

export const userQuestionnaireAnswersSelfRepository = {
  getMy: () =>
    apiClient.get<{ data: UserQuestionnaireAnswerRecord[] }>('/api/user/user-questionnaire-answers').then(wrap),
  /** Filled answers (non-null, non-empty) / total active questions, as percentage. */
  /** Completion % for L4, L5, or both. key = 'L4' | 'L5' | 'ALL'. */
  getCompletion: (key: 'L4' | 'L5' | 'ALL') =>
    apiClient
      .get<{ data: UserQuestionnaireCompletionRecord }>(
        `/api/user/user-questionnaire-answers/assessment-score?key=${encodeURIComponent(key)}`,
      )
      .then(wrap),
  submit: (answers: Array<{ question_id: number; answer: string }>) =>
    apiClient.post<{ data: UserQuestionnaireAnswerRecord[] }>('/api/user/user-questionnaire-answers', { answers }),
}

// --- Professional Qualifications ---
export interface UserProfessionalQualificationsRecord {
  id: number
  user_id: number
  has_experience?: boolean
  years_of_experience?: number | null
  is_currently_employed?: boolean
  current_job_type?: string | null
  current_job_title?: string | null
  native_language?: string | null
  arabic_level?: string | null
  english_level?: string | null
  educational_level?: string | null
  major_field_of_study?: string | null
  cv_file_path?: string | null
  profile_photo_file_path?: string | null
  type?: string | null
  status?: number
  /** When sent on POST/PUT, updates users.job_type (e.g. office_based, field_team) */
  job_type?: string | null
  [key: string]: unknown
}

export const userProfessionalQualificationsSelfRepository = {
  getMy: () =>
    apiClient
      .get<{ data: UserProfessionalQualificationsRecord }>('/api/user/user-professional-qualifications')
      .then(wrap),
  upsertMy: (data: Partial<UserProfessionalQualificationsRecord>) =>
    apiClient
      .post<{ data: UserProfessionalQualificationsRecord }>('/api/user/user-professional-qualifications', data)
      .then(res => res.data),
  replaceMy: (data: Partial<UserProfessionalQualificationsRecord>) =>
    apiClient
      .put<{ data: UserProfessionalQualificationsRecord }>('/api/user/user-professional-qualifications', data)
      .then(res => res.data),
  deleteMy: () =>
    apiClient.delete<{ message: string }>('/api/user/user-professional-qualifications'),
}

// --- Iqama Details ---
export interface UserIqamaDetailsRecord {
  id: number
  user_id?: number | null
  arabic_name_following_iqama?: string | null
  english_name_following_iqama?: string | null
  nationality?: string | null
  marital_status?: string | null
  birthdate?: string | null
  mobile_no?: string | null
  personal_email?: string | null
  education_level_major?: string | null
  education_certificate?: string | null
  iqama_number?: string | null
  iqama_copy?: string | null
  iqama_expiry?: string | null
  profession_as_per_iqama?: string | null
  short_national_address?: string | null
  passport_number?: string | null
  passport_copy?: string | null
  passport_expiry?: string | null
  iban?: string | null
  iban_copy?: string | null
  status?: number
  [key: string]: unknown
}

export const userIqamaDetailsSelfRepository = {
  getMy: () =>
    apiClient.get<{ data: UserIqamaDetailsRecord }>('/api/user/user-iqama-details').then(wrap),
  upsertMy: (data: Partial<UserIqamaDetailsRecord>) =>
    apiClient.post<{ data: UserIqamaDetailsRecord }>('/api/user/user-iqama-details', data).then(res => res.data),
  replaceMy: (data: Partial<UserIqamaDetailsRecord>) =>
    apiClient.put<{ data: UserIqamaDetailsRecord }>('/api/user/user-iqama-details', data).then(res => res.data),
  deleteMy: () =>
    apiClient.delete<{ message: string }>('/api/user/user-iqama-details'),
}

// --- Previous Job Details ---
export interface UserPrevJobDetailsRecord {
  id: number
  user_id?: number | null
  job_title?: string | null
  job_description?: string | null
  region_city?: string | null
  customer?: string | null
  line_manager?: string | null
  annual_leave_days?: number | null
  working_days_per_week?: number | null
  medical_insurance_class?: string | null
  termination_compensation?: string | null
  need_ajeer?: boolean
  cost_study?: boolean
  need_c_store?: boolean
  confirmation_salary_discussion?: boolean
  confirmation_insurance_class_discussion?: boolean
  status?: number
  [key: string]: unknown
}

// --- User Jobs (browse and apply) ---
export interface UserJobApplicationRecord {
  id: number
  job_id: number
  user_id: number
  applied_at?: string
  status: string
  job_title?: string
  job_type?: string
  [key: string]: unknown
}

export const userJobsSelfRepository = {
  list: (params?: { page?: number; limit?: number; status?: string; search?: string; job_type?: string; department?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.status) searchParams.set('status', params.status)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.job_type) searchParams.set('job_type', params.job_type)
    if (params?.department) searchParams.set('department', params.department)
    return apiClient.get<{ data: unknown[]; pagination: { total: number; page: number; limit: number; pages: number } }>(
      `/api/user/jobs?${searchParams.toString()}`
    )
  },
  getOne: (id: number) =>
    apiClient.get<{ data: unknown }>(`/api/user/jobs/${id}`).then(res => res.data),
  apply: (jobId: number) =>
    apiClient.post<{ data: unknown }>(`/api/user/jobs/${jobId}/apply`, {}).then(res => res.data),
  getMyApplications: () =>
    apiClient.get<{ data: UserJobApplicationRecord[] }>('/api/user/jobs/my-applications').then(wrap),
}

export const userPrevJobDetailsSelfRepository = {
  getMy: () =>
    apiClient.get<{ data: UserPrevJobDetailsRecord }>('/api/user/user-prev-job-details').then(wrap),
  upsertMy: (data: Partial<UserPrevJobDetailsRecord>) =>
    apiClient
      .post<{ data: UserPrevJobDetailsRecord }>('/api/user/user-prev-job-details', data)
      .then(res => res.data),
  replaceMy: (data: Partial<UserPrevJobDetailsRecord>) =>
    apiClient
      .put<{ data: UserPrevJobDetailsRecord }>('/api/user/user-prev-job-details', data)
      .then(res => res.data),
  deleteMy: () =>
    apiClient.delete<{ message: string }>('/api/user/user-prev-job-details'),
}

export interface MyProfileRecord {
  user: Record<string, unknown> | null
  iqama_details: UserIqamaDetailsRecord | null
  professional_qualifications: UserProfessionalQualificationsRecord | null
  prev_job_details: UserPrevJobDetailsRecord | null
  assessment_score?: Record<string, unknown>
  profile_completion?: Record<string, unknown>
}

export type PatchProfileChangePayload = {
  user?: Record<string, unknown>
  iqama_details?: Partial<UserIqamaDetailsRecord>
  professional_qualifications?: Partial<UserProfessionalQualificationsRecord>
  prev_job_details?: Partial<UserPrevJobDetailsRecord>
  profile_photo_file_path?: string | null
  [key: string]: unknown
}

export const userProfileSelfRepository = {
  getMy: () =>
    apiClient.get<{ data: MyProfileRecord }>('/api/user/profile').then(wrap),
  patchMyChange: (data: PatchProfileChangePayload) =>
    apiClient.patch<{ data: MyProfileRecord }>('/api/user/profile-change', data).then(res => res.data),
}
